import React, { useState, useRef, useEffect } from "react";
import {
  Paper,
  IconButton,
  TextField,
  CircularProgress,
  Avatar,
  Box,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import MinimizeIcon from "@mui/icons-material/Minimize";
import SendIcon from "@mui/icons-material/Send";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import HubIcon from "@mui/icons-material/Hub";
import PersonIcon from "@mui/icons-material/Person";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import config from "../../../config/Config";

const ChatContainer = styled(Paper)(({ theme, expanded }) => ({
  position: "fixed",
  bottom: expanded ? "10vh" : "60px",
  right: "84px",
  width: expanded ? "854px" : "380px",
  height: expanded ? "85vh" : "min(550px, 70vh)",
  maxHeight: expanded ? "85vh" : "70vh",
  transform: "none",
  borderRadius: "16px",
  boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  zIndex: 1300,
  transition:
    "width 0.4s ease-in-out, height 0.4s ease-in-out, bottom 0.4s ease-in-out, max-height 0.4s ease-in-out",
  opacity: 1,
  "@media (max-width: 1024px)": {
    width: expanded ? "90vw" : "360px",
    height: expanded ? "75vh" : "min(520px, 65vh)",
    maxHeight: expanded ? "75vh" : "65vh",
    right: "20px",
    bottom: expanded ? "5vh" : "70px",
  },
  "@media (max-width: 768px)": {
    width: expanded ? "95vw" : "340px",
    height: expanded ? "80vh" : "min(500px, 60vh)",
    maxHeight: expanded ? "80vh" : "60vh",
    right: "12px",
    bottom: expanded ? "3vh" : "80px",
  },
  "@media (max-width: 480px)": {
    width: expanded ? "100vw" : "90vw",
    height: expanded ? "100vh" : "min(480px, 70vh)",
    maxHeight: expanded ? "100vh" : "70vh",
    right: expanded ? "0" : "5vw",
    bottom: expanded ? "0" : "70px",
    borderRadius: expanded ? "0" : "16px",
  },
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #067de2 0%, #0056b3 100%)",
  color: "#fff",
  padding: "14px 16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontFamily: "Roboto, Helvetica, Arial, sans-serif",
  borderTopLeftRadius: "16px",
  borderTopRightRadius: "16px",
}));

const MessagesContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
  padding: "16px",
  backgroundColor: "#f5f7fa",
  WebkitOverflowScrolling: "touch",
  minHeight: 0,
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#888",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "#555",
  },
  "@media (max-width: 480px)": {
    padding: "12px",
  },
}));

const MessagesWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  minHeight: "100%",
  justifyContent: "flex-end",
});

const MessageBubble = styled(Paper)(({ theme, isUser }) => ({
  padding: "10px 14px",
  maxWidth: "90%",
  alignSelf: isUser ? "flex-end" : "flex-start",
  backgroundColor: isUser ? "#067de2" : "#fff",
  color: isUser ? "#fff" : "#333",
  borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  fontFamily: "Roboto, Helvetica, Arial, sans-serif",
  fontSize: "14px",
  wordWrap: "break-word",
  display: "flex",
  gap: "8px",
  alignItems: "flex-start",
  "@media (max-width: 480px)": {
    maxWidth: "85%",
    fontSize: "13px",
    padding: "8px 12px",
  },
}));

const InputContainer = styled(Box)(({ theme }) => ({
  padding: "12px 16px",
  backgroundColor: "#fff",
  borderTop: "1px solid #e0e0e0",
  display: "flex",
  gap: "10px",
  alignItems: "center",
  borderBottomLeftRadius: "16px",
  borderBottomRightRadius: "16px",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "20px",
    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
    fontSize: "14px",
    backgroundColor: "#f8f9fa",
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#067de2",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#067de2",
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "10px 14px",
  },
}));

const TypingIndicator = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  padding: "8px 0",
  "& .dot": {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: "#067de2",
    animation: "bounce 1.4s infinite ease-in-out both",
  },
  "& .dot:nth-of-type(1)": {
    animationDelay: "-0.32s",
  },
  "& .dot:nth-of-type(2)": {
    animationDelay: "-0.16s",
  },
  "@keyframes bounce": {
    "0%, 80%, 100%": {
      transform: "scale(0)",
      opacity: 0.5,
    },
    "40%": {
      transform: "scale(1)",
      opacity: 1,
    },
  },
});

const SendButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: "#067de2",
  color: "#fff",
  width: "40px",
  height: "40px",
  "&:hover": {
    backgroundColor: "#0056b3",
  },
  "&:disabled": {
    backgroundColor: "#e0e0e0",
    color: "#999",
  },
}));

const HeaderButton = styled(IconButton)({
  color: "#fff",
  padding: "6px",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.1)",
  },
});

// ─── Inline span renderer: bold, italic, inline-code, links ──────────────
// ─── Highlight CALQ IDs inside a plain-text segment ──────────────────────
const highlightCalqIds = (plainText, isUser, keyPrefix) => {
  const CALQ = /\bCALQ\d+\b/gi;
  const result = [];
  let last = 0;
  let m;
  let idx = 0;
  while ((m = CALQ.exec(plainText)) !== null) {
    if (m.index > last) {
      result.push(<span key={`${keyPrefix}-cp${idx}`}>{plainText.slice(last, m.index)}</span>);
    }
    result.push(
      <strong
        key={`${keyPrefix}-calq${idx}`}
        style={{ fontWeight: 700, color: isUser ? "#fff" : "#067de2" }}
      >
        {m[0]}
      </strong>
    );
    last = m.index + m[0].length;
    idx++;
  }
  if (last < plainText.length) {
    result.push(<span key={`${keyPrefix}-ctail${idx}`}>{plainText.slice(last)}</span>);
  }
  return result.length ? result : [<span key={`${keyPrefix}-cplain`}>{plainText}</span>];
};

const renderInlineMarkdown = (text, isUser, keyPrefix = "") => {
  // Pattern order matters: links before bold/italic to avoid partial matches
  const TOKEN = /(\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\))/g;
  const parts = [];
  let last = 0;
  let match;
  let i = 0;

  while ((match = TOKEN.exec(text)) !== null) {
    if (match.index > last) {
      // Scan the plain-text gap for CALQ IDs before pushing
      const gap = text.slice(last, match.index);
      parts.push(...highlightCalqIds(gap, isUser, `${keyPrefix}-g${i}`));
    }
    const [full, , boldItalic, bold, italic, code, linkLabel, linkHref] = match;
    if (boldItalic) {
      parts.push(<strong key={`${keyPrefix}-bi${i}`}><em style={{ color: isUser ? "#fff" : "#067de2" }}>{boldItalic}</em></strong>);
    } else if (bold) {
      parts.push(
        <strong key={`${keyPrefix}-b${i}`} style={{ fontWeight: 700, color: isUser ? "#fff" : "#067de2" }}>
          {bold}
        </strong>
      );
    } else if (italic) {
      parts.push(<em key={`${keyPrefix}-i${i}`} style={{ color: isUser ? "#ffffffcc" : "#555" }}>{italic}</em>);
    } else if (code) {
      parts.push(
        <code
          key={`${keyPrefix}-c${i}`}
          style={{
            fontFamily: "monospace",
            fontSize: "12px",
            backgroundColor: isUser ? "rgba(255,255,255,0.15)" : "#f0f0f0",
            color: isUser ? "#fff" : "#c7254e",
            padding: "1px 4px",
            borderRadius: "3px",
          }}
        >
          {code}
        </code>
      );
    } else if (linkLabel && linkHref) {
      // Only render as a link if it's NOT a report-download URL
      // (those are stripped from the message text before rendering)
      parts.push(
        <a
          key={`${keyPrefix}-a${i}`}
          href={linkHref}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: isUser ? "#fff" : "#067de2",
            textDecoration: "underline",
            wordBreak: "break-all",
          }}
        >
          {linkLabel}
        </a>
      );
    }
    last = match.index + full.length;
    i++;
  }
  // Scan the trailing plain-text segment for CALQ IDs too
  if (last < text.length) {
    parts.push(...highlightCalqIds(text.slice(last), isUser, `${keyPrefix}-tail`));
  }
  return parts.length ? parts : highlightCalqIds(text, isUser, `${keyPrefix}-plain`);
};

// ─── Formatted Message Component ──────────────────────────────────────────
const FormattedMessage = ({ text, isUser }) => {
  if (!text) return null;

  const baseStyle = {
    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
    fontSize: "14px",
    color: isUser ? "#fff" : "#333",
    lineHeight: "21px",
  };

  const lines = text.split("\n");
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const raw = lines[i];
    const trimmed = raw.trim();

    // ── H2 heading: ## Foo
    if (trimmed.startsWith("## ")) {
      const headingText = trimmed.slice(3);
      elements.push(
        <Typography
          key={`h2-${i}`}
          component="div"
          sx={{
            ...baseStyle,
            fontSize: "15px",
            fontWeight: 700,
            color: isUser ? "#fff" : "#067de2",
            mt: elements.length > 0 ? "14px" : 0,
            mb: "4px",
            borderBottom: isUser ? "1px solid rgba(255,255,255,0.3)" : "1px solid #e0e0e0",
            pb: "2px",
          }}
        >
          {renderInlineMarkdown(headingText, isUser, `h2-${i}`)}
        </Typography>
      );
      i++;
      continue;
    }

    // ── H3 heading: ### Foo
    if (trimmed.startsWith("###") ||trimmed.startsWith("####") ) {
      const isH4 = trimmed.startsWith("####");
      const headingText = trimmed.slice(isH4 ? 4 : 3);
      elements.push(
        <Typography
          key={`h3-${i}`}
          component="div"
          sx={{
            ...baseStyle,
            fontSize: "14px",
            fontWeight: 700,
            color: isUser ? "#fff" : "#444",
            mt: elements.length > 0 ? "10px" : 0,
            mb: "2px",
          }}
        >
          {renderInlineMarkdown(headingText, isUser, `h3-${i}`)}
        </Typography>
      );
      i++;
      continue;
    }

    // ── Bullet: - foo  or  • foo  or  * foo
    if (trimmed.match(/^[-•*]\s+/)) {
      const bulletContent = trimmed.replace(/^[-•*]\s+/, "");
      elements.push(
        <Box
          key={`bullet-${i}`}
          sx={{ display: "flex", gap: "8px", mt: "5px", alignItems: "flex-start" }}
        >
          <span style={{ ...baseStyle, minWidth: "14px", flexShrink: 0, marginTop: "1px" }}>•</span>
          <span style={{ ...baseStyle, flex: 1 }}>
            {renderInlineMarkdown(bulletContent, isUser, `bullet-${i}`)}
          </span>
        </Box>
      );
      i++;
      continue;
    }

    // ── Numbered list: 1. foo
    const numberedMatch = trimmed.match(/^(\d+)\.\s+(.+)$/);
    if (numberedMatch) {
      elements.push(
        <Box
          key={`num-${i}`}
          sx={{ display: "flex", gap: "8px", mt: "5px", alignItems: "flex-start" }}
        >
          <span style={{ ...baseStyle, minWidth: "20px", flexShrink: 0, marginTop: "1px" }}>
            {numberedMatch[1]}.
          </span>
          <span style={{ ...baseStyle, flex: 1 }}>
            {renderInlineMarkdown(numberedMatch[2], isUser, `num-${i}`)}
          </span>
        </Box>
      );
      i++;
      continue;
    }

    // ── Horizontal rule: --- or ***
    if (trimmed.match(/^[-*]{3,}$/)) {
      elements.push(
        <Box
          key={`hr-${i}`}
          sx={{ borderTop: isUser ? "1px solid rgba(255,255,255,0.3)" : "1px solid #e0e0e0", my: "8px" }}
        />
      );
      i++;
      continue;
    }

    // ── Empty line → spacing
    if (!trimmed) {
      if (elements.length > 0) {
        elements.push(<Box key={`sp-${i}`} sx={{ height: "6px" }} />);
      }
      i++;
      continue;
    }

    // ── Regular paragraph line
    elements.push(
      <Typography
        key={`p-${i}`}
        component="div"
        sx={{ ...baseStyle, mt: elements.length > 0 ? "5px" : 0 }}
      >
        {renderInlineMarkdown(trimmed, isUser, `p-${i}`)}
      </Typography>
    );
    i++;
  }

  return <Box>{elements}</Box>;
};

const generateUUID = () => {
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
      (
        +c ^
        (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))
      ).toString(16)
    );
  }

  return `fallback-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const AIChatbot = ({ open, onClose, applicationId = null }) => {
  const [messages, setMessages] = useState(() => [
    {
      id: "initial-greeting",
      text: "Hello! I'm your AI assistant. How can I help you today?",
      isUser: false,
      system: true,
      context: "INITIAL",
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [sessionId, setSessionId] = useState(() => {
    try {
      const saved = sessionStorage.getItem("chat_state");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.sessionId) return parsed.sessionId;
      }
    } catch (e) {}
    return generateUUID();
  });
  const [pageContext, setPageContext] = useState("LISTING");
  const [isStreaming, setIsStreaming] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const hasInitializedRef = useRef(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  /**
   * isFirstMessage tracks whether the NEXT user message sent to the backend
   * is the very first one in this application-context session.
   * When true, the applicationId is always appended regardless of keywords.
   */
  const messagesEndRef = useRef(null);
  const contextAnnouncedRef = useRef(false);
  const inputRef = useRef(null);
  const lastApplicationIdRef = useRef(applicationId);
  const streamingMessageIdRef = useRef(null);

  // ── Session ID ────────────────────────────────────────────────────────────
  // useEffect(() => {
  //   if (!sessionId) {
  //     const newSessionId = `session_${Date.now()}_${Math.random()
  //       .toString(36)
  //       .substr(2, 9)}`;
  //     setSessionId(newSessionId);
  //     console.log("newsessionId on Mount of Component", newSessionId)
  //   }
  // }, [sessionId]);


  // ── Restore persisted chat state ──────────────────────────────────────────
  useEffect(() => {
    if (hasInitializedRef.current) return; // Only run once on mount
    
    const saved = sessionStorage.getItem("chat_state");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed?.messages?.length) {
          setMessages(parsed.messages);
        }
        // if (parsed?.sessionId) {
        //   setSessionId(parsed.sessionId);
        // }
      } catch (e) {
        console.error("Failed to restore chat state", e);
      }
    }
    hasInitializedRef.current = true;
  }, []);

  // ── Persist chat state ────────────────────────────────────────────────────
  useEffect(() => {
    if (!sessionId) return;
    sessionStorage.setItem(
      "chat_state",
      JSON.stringify({ messages, sessionId })
    );
  }, [messages, sessionId]);

  // ── Page context ──────────────────────────────────────────────────────────
  useEffect(() => {
    setPageContext(applicationId ? "CREATE" : "LISTING");
  }, [applicationId]);

  // ── Initial greeting is handled inside the applicationId useEffect below ──

  // ── Context greeting when applicationId changes (Create page) ────────────
  useEffect(() => {
  // Only reset first message flag if application actually changed
  const hasChanged = lastApplicationIdRef.current !== applicationId;
  if (hasChanged) {
    lastApplicationIdRef.current = applicationId;
  }

  setMessages((prev) => {
    // Find last system message
    const lastSystemEntry = [...prev]
      .map((m, i) => ({ m, i }))
      .reverse()
      .find(({ m }) => m.system);

    const lastSystemIndex = lastSystemEntry?.i;
    const lastSystemMsg = lastSystemEntry?.m;

    const hasUserAfterContext =
      lastSystemIndex !== undefined &&
      prev.slice(lastSystemIndex + 1).some((m) => m.isUser);

    const nextContext = applicationId
      ? {
          id: `context-${applicationId}`,
          text: `Hello, I am your AI Assistant.\nYou are now viewing Application ${applicationId}.\n\nHow can I help you today?`,
          isUser: false,
          system: true,
          context: applicationId,
          timestamp: new Date(),
        }
      : {
          id: `listing-${Date.now()}`,
          text:
            "Hello, I am your AI Assistant. How can I help you?\nYou are currently viewing the Trade-In Application list.",
          isUser: false,
          system: true,
          context: "LISTING",
          timestamp: new Date(),
        };

    // Same context → do nothing
    if (lastSystemMsg?.context === nextContext.context) {
      return prev;
    }

    // User interacted → append new context message
    if (hasUserAfterContext) {
      return [...prev, nextContext];
    }

    // Replace unused context
    if (lastSystemIndex !== undefined) {
      return [...prev.slice(0, lastSystemIndex), nextContext];
    }

    return [...prev, nextContext];
  });
}, [applicationId]);


  useEffect(() => {
    contextAnnouncedRef.current = false;
  }, [applicationId]);

  // ── Auto-scroll ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: "auto",
        block: "end",
      });
    }
  }, [messages, isLoading, open]);

  // ── Focus input on open ───────────────────────────────────────────────────
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar((s) => ({ ...s, open: false }));
  };

  const extractDownloadUrl = (text) => {
    if (!text) return null;
    // Only match the specific report-download endpoint, not arbitrary URLs in the response body
    const match = text.match(/https?:\/\/[^\s]*\/chat\/report\/download[^\s]*/);
    return match ? match[0].replace(/[\s\n\r]+$/, "") : null;
  };

  const extractFileType = (text) => {
    if (!text) return null;
    // Primary: read the format= query param directly from the URL in the text
    const formatMatch = text.match(/[?&]format=(xlsx|pdf)/i);
    if (formatMatch) {
      return formatMatch[1].toLowerCase() === "pdf" ? "pdf" : "excel";
    }
    // Fallback: keyword scan of the surrounding prose
    const lower = text.toLowerCase();
    if (lower.includes("pdf")) return "pdf";
    if (lower.includes("excel") || lower.includes("xlsx")) return "excel";
    return null;
  };

  const buildQueryForBackend = (rawQuery) => {
    return rawQuery;
  };

  // ─── Send message (streaming) ─────────────────────────────────────────────
  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const trimmedInput = inputValue.trim();
    const enhancedQuery = buildQueryForBackend(trimmedInput);

    const userMessage = {
      id: Date.now(),
      text: trimmedInput,          
      isUser: true,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");

    // Scroll after adding user message
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }, 100);

    setIsLoading(true);
    setIsStreaming(true);
    setStatusText("Thinking...");

    try {
      const tokenStr = localStorage.getItem("access_token");
      const response = await fetch(`${config.calqueAI}chat/query`, {
        method: "POST",
        headers: {
          Accept: "text/event-stream",
          Authorization: `Bearer ${tokenStr}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: enhancedQuery,
          session_id: sessionId,
          application_id: applicationId,
        }),
      });

      if (response.status === 403) {
        localStorage.setItem("access_token", "");
        window.location.href = "/";
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to get response");
      }

      // ── Stream processing ──────────────────────────────────────────────
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = "";
      // Generate a stable id for the streaming AI message
      const aiMessageId = Date.now() + 1;
      streamingMessageIdRef.current = aiMessageId;
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        // Keep the last (potentially incomplete) line in the buffer
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;

          let event;
          try {
            event = JSON.parse(raw);
          } catch {
            continue;
          }

          if (event.type === "status") {
            // Update the status label shown in the typing bubble
            setStatusText(event.text || "Thinking...");
          }

          if (event.type === "token") {
            // First token → hide status bubble, create the AI message
            setStatusText("");
            setIsLoading(false);
            accumulatedText += event.text;

            setMessages((prev) => {
              const exists = prev.find((m) => m.id === aiMessageId);
              if (exists) {
                // Update text in-place
                return prev.map((m) =>
                  m.id === aiMessageId ? { ...m, text: accumulatedText } : m
                );
              }
              // First token — add the message
              return [
                ...prev,
                {
                  id: aiMessageId,
                  text: accumulatedText,
                  isUser: false,
                  timestamp: new Date(),
                },
              ];
            });

            // Keep scrolled to bottom as tokens arrive
            setTimeout(() => {
              messagesEndRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "end",
              });
            }, 50);
          }

          if (event.type === "done") {
            // Safety: always clear status / loading on done
            setStatusText("");
            setIsLoading(false);
            setIsStreaming(false);

            // Finalise: extract download URL if present in the accumulated text
            const downloadUrl = extractDownloadUrl(accumulatedText);
            // Derive file type from the URL's format= param (xlsx or pdf)
            const fileType = downloadUrl
              ? extractFileType(downloadUrl)
              : extractFileType(accumulatedText);

            if (downloadUrl) {
              // Strip the raw URL from the visible message text
              const cleanText = accumulatedText
                .replace(downloadUrl, "")
                .replace(/\n{2,}/g, "\n")
                .trim();
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === aiMessageId
                    ? { ...m, text: cleanText, downloadUrl, fileType: fileType || "excel" }
                    : m
                )
              );
            }
            // Extract session_id if backend sent it inside the done event
            if (event.session_id && event.session_id !== sessionId) {
              setSessionId(event.session_id);
            }
          }

          if (event.type === "error") {
            setStatusText("");
            setIsLoading(false);
            setIsStreaming(false);
            setMessages((prev) => [
              ...prev,
              {
                id: Date.now() + 2,
                text: event.text || "Sorry, I encountered an error. Please try again.",
                isUser: false,
                timestamp: new Date(),
              },
            ]);
            showSnackbar("Failed to send message", "error");
          }
        }
      }

      // Flush any remaining buffer content after stream ends
      if (buffer.startsWith("data: ")) {
        try {
          const event = JSON.parse(buffer.slice(6).trim());
          if (event.type === "done") {
            setStatusText("");
            setIsLoading(false);
          }
        } catch {
          // ignore malformed trailing data
        }
      }

      // If no tokens were ever received (pure status → done flow), clear loading
      setStatusText("");
      setIsLoading(false);
      setIsStreaming(false);

    } catch (error) {
      console.error("Chatbot error:", error);
      setStatusText("");
      setIsLoading(false);
      setIsStreaming(false);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          text: "Sorry, I encountered an error. Please try again.",
          isUser: false,
          timestamp: new Date(),
        },
      ]);
      showSnackbar("Failed to send message", "error");
    }
  };

  // ─── Download helper ──────────────────────────────────────────────────────
const handleDownloadFromUrl = async (url, fileType = "excel") => {
  try {
    const tokenStr = localStorage.getItem("access_token");

    const acceptHeader =
      fileType === "pdf"
        ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${tokenStr}`,
        Accept: acceptHeader,
      },
    });

    if (!response.ok) throw new Error("Download failed");

    const blob = await response.blob();
    const disposition = response.headers.get("Content-Disposition");

    let filename =
      fileType === "pdf"
        ? `Calque_Report_${new Date().toISOString().slice(0, 10)}.pdf`
        : `Calque_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;

    if (disposition) {
      const match = disposition.match(/filename="?([^"]+)"?/);
      if (match?.[1]) filename = match[1];
    }

    const mimeType =
      fileType === "pdf"
        ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    const dlUrl = window.URL.createObjectURL(new Blob([blob], { type: mimeType }));
    const a = document.createElement("a");
    a.href = dlUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(dlUrl);
    showSnackbar("Report downloaded successfully", "success");
  } catch (error) {
    console.error(error);
    showSnackbar("Failed to download report", "error");
  }
};

  // ─── Keyboard ─────────────────────────────────────────────────────────────
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // ─── Clear session ────────────────────────────────────────────────────────
  const handleDeleteSession = async () => {
    if (!sessionId) {
      showSnackbar("No session to clear", "warning");
      return;
    }

    if (!window.confirm("Are you sure you want to clear this chat session?")) {
      return;
    }

    const resetMessages = () => {
      if (applicationId) {
        setMessages([
          {
            id: `context-${applicationId}`,
            text: `Hello I am your AI Assistant.\nYou are now viewing application ${applicationId}.\n\nHow can I help you today?`,
            isUser: false,
            system: true,
            context: applicationId,
            timestamp: new Date(),
          },
        ]);
      } else {
        setMessages([
          {
            id: Date.now(),
            text: "Hello! I'm your AI assistant. How can I help you today?",
            isUser: false,
            timestamp: new Date(),
          },
        ]);
      }

      // const newSessionId = `session_${Date.now()}_${Math.random()
      //   .toString(36)
      //   .substr(2, 9)}`;
      // setSessionId(newSessionId);
      const newSessionId = generateUUID();
      setSessionId(newSessionId);

      // Reset first-message flag after clearing
      // if (applicationId) setIsFirstMessage(true);
    };

    try {
      const tokenStr = localStorage.getItem("access_token");
      // const cleanSessionId = sessionId.replace("session_", "");
      const cleanSessionId = sessionId;

      const response = await fetch(
        `${config.calqueAI}chat/session/${cleanSessionId}`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${tokenStr}`,
          },
        }
      );

      if ([200, 204, 404].includes(response.status)) {
        resetMessages();
        showSnackbar("Chat session cleared", "success");
      } else if (response.status === 403) {
        localStorage.setItem("access_token", "");
        window.location.href = "/";
      } else {
        throw new Error("Failed to delete session");
      }
    } catch (error) {
      console.error("Delete error:", error);
      resetMessages();
      showSnackbar("Chat session cleared locally", "info");
    }
  };

  // ─── UI handlers ──────────────────────────────────────────────────────────
  const handleClose = () => {
    onClose();
    setExpanded(false);
  };

  const handleMinimize = () => {
    onClose();
    setExpanded(false);
  };

  const toggleExpanded = () => setExpanded((e) => !e);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <Box sx={{ display: open ? "flex" : "none" }}>
        <ChatContainer elevation={8} expanded={expanded}>
          <ChatHeader>
            <Box display="flex" alignItems="center" gap={1}>
              <HubIcon sx={{ fontSize: 24 }} />
              <Typography
                variant="h6"
                sx={{ fontFamily: "Roboto, Helvetica, Arial, sans-serif", fontSize: "16px" }}
              >
                Calque AI Assistant
              </Typography>
            </Box>
            <Box display="flex" gap={0.5}>
              <HeaderButton
                onClick={handleDeleteSession}
                size="small"
                title="Clear Chat"
              >
                <CleaningServicesIcon fontSize="small" />
              </HeaderButton>
              <HeaderButton
                onClick={toggleExpanded}
                size="small"
                title={expanded ? "Contract" : "Expand"}
              >
                {expanded ? (
                  <CloseFullscreenIcon fontSize="small" />
                ) : (
                  <OpenInFullIcon fontSize="small" />
                )}
              </HeaderButton>
              <HeaderButton onClick={handleMinimize} size="small" title="Minimize">
                <MinimizeIcon
                  sx={{ position: "relative", top: "-5px" }}
                  fontSize="small"
                />
              </HeaderButton>
              <HeaderButton onClick={handleClose} size="small" title="Close">
                <CloseIcon fontSize="small" />
              </HeaderButton>
            </Box>
          </ChatHeader>

          <MessagesContainer>
            <MessagesWrapper>
              {messages.map((message) => (
                <MessageBubble key={message.id} isUser={message.isUser} elevation={1}>
                  <Avatar
                    sx={{
                      width: 26,
                      height: 26,
                      backgroundColor: message.isUser ? "#fff" : "#067de2",
                      color: message.isUser ? "#067de2" : "#fff",
                    }}
                  >
                    {message.isUser ? (
                      <PersonIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <HubIcon sx={{ fontSize: 17 }} />
                    )}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <FormattedMessage
                      text={message.text}
                      isUser={message.isUser}
                    />

                    {message.downloadUrl && (
                      <Box mt={1}>
                        <IconButton
                          onClick={() =>
                            handleDownloadFromUrl(message.downloadUrl, message.fileType)
                          }
                          sx={{
                            backgroundColor: "#067de2",
                            color: "#fff",
                            borderRadius: "8px",
                            padding: "6px 12px",
                            fontSize: "13px",
                            "&:hover": { backgroundColor: "#0056b3" },
                          }}
                        >
                          {message.fileType==="pdf"?"Download Pdf":"Download Excel"}
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </MessageBubble>
              ))}

              {/* ── Status / typing indicator ─────────────────────────────── */}
              {(isLoading || statusText) && (
                <MessageBubble isUser={false} elevation={1}>
                  <Avatar
                    sx={{
                      width: 26,
                      height: 26,
                      backgroundColor: "#067de2",
                      color: "#fff",
                    }}
                  >
                    <HubIcon sx={{ fontSize: 17 }} />
                  </Avatar>
                  {statusText ? (
                    // Show the status label (e.g. "Thinking...", "Searching...")
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 0" }}>
                      <Typography
                        sx={{
                          fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                          fontSize: "13px",
                          color: "#555",
                          fontStyle: "italic",
                          display:"flex",
                        }}
                      >
                        {statusText}
                        <TypingIndicator sx={{marginLeft:"10px"}}>
                        <div className="dot" />
                        <div className="dot" />
                        <div className="dot" />
                      </TypingIndicator>
                      </Typography>
                    </Box>
                  ) : (
                    // Fallback plain dots when loading but no status yet
                    <TypingIndicator>
                      <div className="dot" />
                      <div className="dot" />
                      <div className="dot" />
                    </TypingIndicator>
                  )}
                </MessageBubble>
              )}

              <div
                ref={messagesEndRef}
                style={{ float: "left", clear: "both", height: "1px" }}
              />
            </MessagesWrapper>
          </MessagesContainer>

          <InputContainer>
            <StyledTextField
              fullWidth
              multiline
              maxRows={3}
              placeholder={
                applicationId
                  ? `Ask about ${applicationId}...`
                  : "Type your message..."
              }
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading || !!statusText || isStreaming}
              inputRef={inputRef}
              size="small"
            />
            <SendButton
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading || !!statusText}
              size="small"
            >
              {isLoading ||isStreaming || statusText ? (
                <CircularProgress size={18} sx={{ color: "#fff" }} />
              ) : (
                <SendIcon sx={{ fontSize: 18 }} />
              )}
            </SendButton>
          </InputContainer>
        </ChatContainer>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AIChatbot;
